import { LayoutDashboard, Users, CheckSquare, Calendar, BookOpen, Building2, Video, Wand2, NotebookPen, FileSignature, UsersRound, TrendingUp, MessageSquare, Send, Sparkles, Truck, Palette, Image as ImageIcon, Trophy, Gift, Award, GraduationCap } from "lucide-react";

// Feature → nav item mapping. Used for CUSTOM roles AND for admin-configurable
// built-in role overrides.
const FEATURE_NAV = {
  tasks: { to: "/dashboard/tasks", label: "Tasks", Icon: CheckSquare, testid: "tasks" },
  meetings: { to: "/dashboard/meetings", label: "Meetings", Icon: Video, testid: "meetings" },
  documents: { to: "/dashboard/documents", label: "Documents", Icon: FileSignature, testid: "documents" },
  notes: { to: "/dashboard/notes", label: "Notes", Icon: NotebookPen, testid: "notes" },
  ai: { to: "/dashboard/ai", label: "AI Content", Icon: Wand2, testid: "ai" },
  team: { to: "/dashboard/team", label: "My Team", Icon: UsersRound, testid: "team" },
  blogs: { to: "/dashboard/blogs", label: "Blogs", Icon: BookOpen, testid: "blogs" },
  schools: { to: "/dashboard/schools", label: "Schools", Icon: Building2, testid: "schools" },
  calendar: { to: "/dashboard/social-calendar", label: "Social Calendar", Icon: Calendar, testid: "calendar" },
  events: { to: "/dashboard/events", label: "Events", Icon: Calendar, testid: "events" },
  vendors: { to: "/dashboard/vendors", label: "Vendors", Icon: Truck, testid: "vendors" },
  design_gallery: { to: "/dashboard/design-gallery", label: "Design Gallery", Icon: ImageIcon, testid: "design-gallery" },
  scheduler: { to: "/dashboard/scheduler", label: "Scheduler", Icon: Calendar, testid: "scheduler" },
};

// Default sidebar for built-in roles. Admin can override via role_overrides.
const DEFAULT_BUILTIN_FEATURES = {
  admin: ["team", "chat", "reports", "social-timeline", "documents", "blog-workflow", "notes", "ai", "schools", "vendors", "member-directory", "all-tasks", "scheduler", "meetings"],
  regional_pm: ["schools", "vendors", "team", "chat", "social-timeline", "documents", "notes", "ai", "tasks", "scheduler", "meetings"],
  project_manager: ["schools", "vendors", "team", "chat", "social-timeline", "documents", "notes", "ai", "tasks", "scheduler", "meetings"],
  social_media_manager: ["team", "chat", "social-timeline", "notes", "ai", "tasks", "scheduler", "meetings"],
  social_media_executive: ["chat", "notes", "ai", "tasks", "scheduler", "meetings"],
  head_media_comm: ["blog-workflow", "team", "chat", "documents", "notes", "ai", "tasks", "scheduler", "meetings"],
  media_comm_executive: ["blog-workflow", "chat", "documents", "notes", "ai", "tasks", "scheduler", "meetings"],
  content_writer: ["blog-workflow", "chat", "documents", "notes", "ai", "tasks", "scheduler", "meetings"],
  head_academics: ["chat", "documents", "notes", "ai", "tasks", "scheduler", "meetings"],
  seo: ["chat", "notes", "tasks", "scheduler", "meetings"],
  school_principal: ["todos", "chat", "scheduler", "meetings"],
  designer: ["design_gallery", "documents", "notes", "chat", "tasks", "scheduler", "meetings"],
  zonal_pm: ["schools", "vendors", "chat", "documents", "notes", "tasks", "scheduler", "meetings"],
  accountant: ["scheduler", "meetings"],
};

// Nav items that aren't in FEATURE_NAV (built-in-only extras).
const BUILTIN_EXTRA_NAV = {
  reports: { to: "/dashboard/reports", label: "Reports", Icon: TrendingUp, testid: "reports" },
  chat: { to: "/dashboard/chat", label: "Chat", Icon: MessageSquare, testid: "chat" },
  "social-timeline": { to: "/dashboard/social-timeline", label: "Social Timeline", Icon: Calendar, testid: "social-timeline" },
  "blog-workflow": { to: "/dashboard/blog-workflow", label: "Blog Workflow", Icon: Send, testid: "blog-workflow" },
  "all-tasks": { to: "/dashboard/all-tasks", label: "All Tasks", Icon: CheckSquare, testid: "all-tasks" },
  todos: { to: "/dashboard/todos", label: "School To-Dos", Icon: CheckSquare, testid: "todos" },
  "member-directory": { to: "/dashboard/members", label: "Member Directory", Icon: Users, testid: "member-directory" },
  scheduler: { to: "/dashboard/scheduler", label: "Scheduler", Icon: Calendar, testid: "scheduler" },
};

const OVERVIEW = {
  admin: { to: "/dashboard", end: true, label: "Overview", Icon: LayoutDashboard, testid: "overview" },
  regional_pm: { to: "/dashboard", end: true, label: "Regional Overview", Icon: LayoutDashboard, testid: "overview" },
  project_manager: { to: "/dashboard", end: true, label: "My Events", Icon: LayoutDashboard, testid: "overview" },
  social_media_manager: { to: "/dashboard", end: true, label: "Calendar", Icon: Calendar, testid: "overview" },
  social_media_executive: { to: "/dashboard", end: true, label: "Calendar", Icon: Calendar, testid: "overview" },
  head_media_comm: { to: "/dashboard", end: true, label: "Blog Editor", Icon: BookOpen, testid: "overview" },
  media_comm_executive: { to: "/dashboard", end: true, label: "Blog Editor", Icon: BookOpen, testid: "overview" },
  content_writer: { to: "/dashboard", end: true, label: "Blog Editor", Icon: BookOpen, testid: "overview" },
  head_academics: { to: "/dashboard", end: true, label: "Strategy Desk", Icon: LayoutDashboard, testid: "overview" },
  seo: { to: "/dashboard", end: true, label: "SEO Desk", Icon: TrendingUp, testid: "overview" },
  school_principal: { to: "/dashboard", end: true, label: "Event Overview", Icon: LayoutDashboard, testid: "overview" },
  designer: { to: "/dashboard", end: true, label: "My Events", Icon: Palette, testid: "overview" },
  accountant: { to: "/dashboard", end: true, label: "Books & Receipts", Icon: LayoutDashboard, testid: "overview" },
};


// Read role overrides (populated by AuthContext / dashboard bootstrap). Falls back to default.
let __roleOverrides = {};
export function setRoleOverrides(overrides) { __roleOverrides = overrides || {}; }
export function getRoleFeatures(role) {
  const override = __roleOverrides[role];
  if (override && Array.isArray(override.features) && override.features.length) return override.features;
  return DEFAULT_BUILTIN_FEATURES[role] || [];
}

// Sidebar sections (heading → feature keys, in order). Items with no matching
// section land in the top "main" block. Chat, Reports and Profile live in the header.
const SECTIONS = [
  { key: "main", title: null, features: ["events", "schools", "vendors", "team", "member-directory", "todos"] },
  { key: "work", title: "Work", features: ["tasks", "all-tasks", "scheduler", "meetings", "notes"] },
  { key: "content", title: "Content & Media", features: ["calendar", "social-timeline", "blogs", "blog-workflow", "documents", "ai", "design_gallery"] },
];
const ITEM_COLORS = {
  events: "bg-pink-500", schools: "bg-sky-500", vendors: "bg-amber-500", team: "bg-indigo-500", "member-directory": "bg-teal-500", todos: "bg-lime-500",
  tasks: "bg-emerald-500", "all-tasks": "bg-emerald-600", scheduler: "bg-orange-500", meetings: "bg-violet-500", notes: "bg-yellow-500",
  calendar: "bg-fuchsia-500", "social-timeline": "bg-purple-500", blogs: "bg-rose-500", "blog-workflow": "bg-red-500", documents: "bg-slate-500", ai: "bg-cyan-500", design_gallery: "bg-pink-600",
};
const HEADER_ONLY = new Set(["chat", "reports"]);

// Growth & recognition pages — every team member gets these (not school principals).
export const GROWTH_NAV = [
  { to: "/dashboard/leaderboard", label: "Leaderboard", Icon: Trophy, testid: "leaderboard", color: "bg-amber-500" },
  { to: "/dashboard/rewards", label: "Rewards", Icon: Gift, testid: "rewards", color: "bg-lime-500" },
  { to: "/dashboard/recognitions", label: "Recognitions", Icon: Award, testid: "recognitions", color: "bg-orange-500" },
  { to: "/dashboard/learn", label: "Learn Dashboard", Icon: GraduationCap, testid: "learn", color: "bg-pink-500" },
];

function featuresFor(role, customRole) {
  if (customRole && Array.isArray(customRole.features)) {
    const f = [...customRole.features];
    if (!f.includes("meetings")) f.push("meetings");
    return { features: f, overview: { to: "/dashboard", end: true, label: customRole.label || "Dashboard", Icon: Sparkles, testid: "overview" } };
  }
  return { features: getRoleFeatures(role), overview: OVERVIEW[role] || null };
}

// Grouped sidebar: [{ key, title, items: [...] }]. Each item has a tile colour.
export function getNavSections(role, customRole = null) {
  const { features, overview } = featuresFor(role, customRole);
  const byFeature = (f) => {
    const item = FEATURE_NAV[f] || BUILTIN_EXTRA_NAV[f];
    return item ? { ...item, color: ITEM_COLORS[f] || "bg-slate-500" } : null;
  };
  const placed = new Set();
  const sections = SECTIONS.map((sec) => ({
    key: sec.key, title: sec.title,
    items: sec.features.filter((f) => features.includes(f) && !placed.has(f) && placed.add(f)).map(byFeature).filter(Boolean),
  }));
  const leftovers = features.filter((f) => !placed.has(f) && !HEADER_ONLY.has(f) && f !== "profile").map(byFeature).filter(Boolean);
  sections[0].items.push(...leftovers);
  if (overview) sections[0].items.unshift({ ...overview, color: "bg-blue-500" });
  if (role !== "school_principal") sections.push({ key: "growth", title: "Growth & Skills", items: GROWTH_NAV });
  return sections.filter((s) => s.items.length);
}

// Header shortcuts this user may see.
export function hasReports(role, customRole = null) {
  return featuresFor(role, customRole).features.includes("reports");
}

// Flat list (kept for pages that still pass navItems / for header search).
export function getNav(role, customRole = null) {
  return getNavSections(role, customRole).flatMap((s) => s.items);
}

// Exposed so admin UI can list every editable built-in role and its features.
export const BUILTIN_ROLE_FEATURE_DEFAULTS = DEFAULT_BUILTIN_FEATURES;
export const ALL_FEATURE_KEYS = [
  ...Object.keys(FEATURE_NAV),
  ...Object.keys(BUILTIN_EXTRA_NAV),
];
